'use client';
import {getStrapiData} from '@/app/lib/api';
import {cn} from '@/lib/utils';
import {StrapiNews} from '@/types/strapi';
import parse from 'html-react-parser';
import {useLocale} from 'next-intl';
import Link from 'next/link';
import {useSearchParams} from 'next/navigation';
import {useEffect, useState} from 'react';
import {dummyImages} from '../helpers/utils';
import DateBlock from '../tools/DateBlock';
import {LazyImage} from '../tools/LazyImage';
import PageTitle from '../tools/PageTitle';
import ComingSoon from './ComingSoon';
import {formDummyRows, LoadingItem} from './LoadingItem';
import Pagination from './Pagination';

export function cleanContent(content: string): string {
  if (!content) return '';
  return (
    content
      .replace(/\[([a-zA-Z0-9_\-]+)[^\]]*\][\s\S]*?\[\/\1\]/g, ' ') // Remove the shortcode pair [xxx]...[/xxx] and all its contents
      .replace(/\[[^\]]+\]/g, ' ') // Remove a single shortcode tag (such as [module:xxx], [h=2], etc.)
      .replace(/&nbsp;/gi, ' ') // Handle common HTML entities
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'")
      .replace(/<[^>]+>/g, ' ') // Remove HTML tags (replace with spaces to avoid text from merging)
      .replace(/\s+/g, ' ') // Remove extra spaces and flatten them into a single space
      .trim()
  );
}

export function truncateByWord(text: string, maxWords: number): string {
  if (!text) return '';

  // 1. 英文單字或數字（包括連字號、縮寫撇號，如 ClimatePartner、COVID-19、Watson's、2026）
  // 2. 中文字符（CJK 統一表意文字，相容 ES5 編譯環境）
  // 3. 其餘非空白字符（標點符號等）
  const tokenRegex =
    /[a-zA-Z0-9]+(?:['’\-+][a-zA-Z0-9]+)*|[\u4e00-\u9fff\u3400-\u4dbf\uf900-\ufaff]|\S/g;

  let wordCount = 0;
  let cutIndex = 0;
  let match: RegExpExecArray | null;
  let hasMore = false;

  while ((match = tokenRegex.exec(text)) !== null) {
    const token = match[0];
    const isWord =
      /^[a-zA-Z0-9]|^[\u4e00-\u9fff\u3400-\u4dbf\uf900-\ufaff]/.test(token);

    if (isWord) {
      if (wordCount >= maxWords) {
        hasMore = true;
        break;
      }
      wordCount++;
    }
    cutIndex = match.index + token.length;
  }

  if (!hasMore && cutIndex >= text.length) {
    return text;
  }

  let result = text.slice(0, cutIndex).trimEnd();
  result = result.replace(/[，,、。！？\.\s]+$/, '');
  return `${result}...`;
}

export default function LatestNewsList() {
  const locale = useLocale();
  const [searchResults, setSearchResults] = useState<StrapiNews[]>([]);
  const searchParams = useSearchParams();
  const [currentPage, setCurrentPage] = useState(() => {
    return parseInt(searchParams.get('p') || '1');
  });
  const [isLoading, setIsLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);

  const maxDescriptionLength =
    {
      en: 50,
      zh: 100
    }[locale] ?? 100;

  const descriptionHandler = (content: string | null | undefined) => {
    if (!content) return '';
    const cleaned = cleanContent(content);
    return truncateByWord(cleaned, maxDescriptionLength);
  };

  const getSearchResult = async () => {
    setIsLoading(true);
    const postData = await getStrapiData(locale, 'posts', {
      filters: {
        post_type: {
          $containsi: 'news'
        },
        posted_at: {
          $lte: new Date()
        }
      },
      populate: ['thumbnail', 'cover'],
      pagination: {
        page: currentPage,
        pageSize: 10
      },
      sort: ['posted_at:desc']
    });

    const totalPages = postData?.meta?.pagination?.pageCount || 1;
    setTotalPages(totalPages);
    setSearchResults(postData?.data || []);
    setIsLoading(false);
  };

  useEffect(() => {
    getSearchResult();
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set('p', currentPage.toString());
    window.history.pushState({}, '', url.href);
    getSearchResult();
  }, [currentPage]);

  return (
    <div className="questrial text-[18px]">
      <div>
        {!isLoading && searchResults.length === 0 && <ComingSoon />}
        <div className="flex flex-col divider divide-y divide-gray-200">
          {isLoading && (
            <LoadingItem>
              <div className="flex flex-col mt-4">
                <div>
                  <div className="h-[24px] bg-gray-100 rounded-full w-3/4 mb-2"></div>
                  <div className="h-[16px] bg-gray-100 rounded-full w-1/2 mb-2"></div>
                </div>

                <div className="my-4 flex gap-4 md:items-start">
                  <div className="w-[30%] md:w-[25%] ">
                    <LazyImage className="aspect-square rounded-lg flex" />
                  </div>
                  <div className="flex flex-col text-gray-600 w-full text-xs md:text-sm gap-2 ">
                    {formDummyRows(5)}
                  </div>
                </div>
              </div>
            </LoadingItem>
          )}
          {!isLoading &&
            searchResults.map((result: StrapiNews, index) => (
              <div key={index} className="flex flex-col ">
                <Link
                  prefetch={false}
                  href={'/latest-news/' + result.slug}
                  className="fade-in-up"
                >
                  <div className="flex flex-col ">
                    <div>
                      <h2
                        className={cn(
                          'text-[24px] text-[#e31836] mt-10',
                          locale == 'en' ? 'font-[700]' : 'font-[500]'
                        )}
                      >
                        <PageTitle
                          title={result.title}
                          className="text-[#e31836] text-[30px] mt-[20px] md:mt-0 "
                        />
                      </h2>
                      <div className="text-[12px]">
                        <DateBlock date={result.posted_at} />
                      </div>
                    </div>

                    <div
                      className={cn(
                        'my-4 md:flex md:flex-cols-2 gap-4 md:items-start',
                        result?.cover?.url ? 'flex-col' : ''
                      )}
                    >
                      {result?.cover ? (
                        <div className="w-full ">
                          <img
                            src={
                              result?.cover?.url ??
                              dummyImages[index % dummyImages.length].src
                            }
                            alt={result.title}
                            className="min-w-[125px] float-left mr-4 mb-4 rounded-sm object-cover object-center w-full"
                          />
                        </div>
                      ) : (
                        <div className="w-[30%] md:w-[25%] ">
                          <img
                            src={
                              result.thumbnail?.url ??
                              dummyImages[index % dummyImages.length].src
                            }
                            alt={result.title}
                            className="min-w-[125px] float-left mr-4 mb-4 rounded-sm object-cover object-center aspect-square"
                          />
                        </div>
                      )}
                      <div className="text-gray-600 w-full">
                        {result.abstract
                          ? descriptionHandler(result.abstract)
                          : parse(descriptionHandler(result.content))}
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            ))}
        </div>
        {!isLoading && searchResults.length > 0 && (
          <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        )}
      </div>
    </div>
  );
}
